import { createHideableComponent } from "@react-aria/collections";
import { TabPanel, TabPanels, Tabs } from "react-aria-components";
import { unstable_batchedUpdates } from "react-dom";
import { useCallback, useMemo, useState } from "react";

import { Tab2 } from "@/ui2/atoms/Tab";
import { TabList2 } from "@/ui2/atoms/TabList";
import { useCosignatureQueue } from "@/features/cosignature/api/queries/useCosignatureQueue";
import { useNeedsReviewQueue } from "@/features/cosignature/api/queries/useNeedsReviewQueue";

import type { AdaptedQueueRow } from "./(table)/helpers/adapters";
import { CosignatureModalProvider } from "./CosignatureModalProvider";
import type { CosignatureTab } from "./types";
import { CosignatureTabEnum } from "./constants";
import { CosignatureTable } from "./(table)/CosignatureTable";
import { Filters } from "./(filters)/Filters";
import { NeedsReviewTab } from "./components/NeedsReviewTab";
import { useCosignatureFilters } from "./(filters)/hooks/useCosignatureFilters";

type Props = { defaultSupervisorId: string };

export function CosignatureTabs({ defaultSupervisorId }: Props) {
  const [tab, setTab] = useState<CosignatureTab>("needs-review");
  const [selectedRows, setSelectedRows] = useState<AdaptedQueueRow[]>([]);

  const selectedIds = selectedRows.map((r) => r.id);

  const selectedItems = useMemo(
    () =>
      selectedRows.map(({ documentType, id, patientId }) => ({
        documentId: id,
        documentType,
        patientId,
      })),
    [selectedRows],
  );

  const [{ status, supervisor_id: supervisorIdFilter }, setFilters, resetFilters] =
    useCosignatureFilters({
      status: null,
      supervisor_id: defaultSupervisorId,
    });

  // null    → explicitly cleared: omit param, fetch all supervisors
  // string  → user-selected or default: use as-is
  const supervisorIdParam = supervisorIdFilter ?? undefined;

  const needsReviewQuery = useNeedsReviewQueue({
    cosignature_status: status ?? undefined,
    supervisor_id: supervisorIdParam,
  });

  const allNotesQuery = useCosignatureQueue({
    enabled: tab === CosignatureTabEnum.ALL_NOTES,
    searchParams: {
      cosignature_status: status ?? undefined,
      supervisor_id: supervisorIdParam,
    },
  });

  const handleTabSelectionChange = useCallback(
    (key: Nullable<CosignatureTab>) => {
      setTab(key ?? "needs-review");
      setFilters({ status: null });
      setSelectedRows([]);
    },
    [setFilters],
  );

  const isFiltered = status !== null || supervisorIdFilter != null;

  return (
    <CosignatureModalProvider onBatchSuccess={() => setSelectedRows([])}>
      <Tabs
        className="tw:flex tw:flex-col tw:gap-4"
        keyboardActivation="manual"
        onSelectionChange={handleTabSelectionChange}
        selectedKey={tab}
      >
        <TabList2 aria-label="Co-signature notes" className="tw:px-6">
          <NeedsReviewTab
            count={needsReviewQuery.data?.length ?? 0}
            isLoading={needsReviewQuery.isLoading}
          />
          <Tab2 id="all-notes">All Notes</Tab2>
        </TabList2>

        <SafeFilters
          onStatusChange={(value) =>
            unstable_batchedUpdates(() => {
              setFilters({ status: value });
              setSelectedRows([]);
            })
          }
          onSupervisorChange={(value) =>
            unstable_batchedUpdates(() => {
              setFilters({ supervisor_id: value });
              setSelectedRows([]);
            })
          }
          selectedItems={selectedItems}
          status={status}
          supervisor_id={supervisorIdFilter}
          tab={tab}
        />

        <TabPanels>
          <TabPanel id="needs-review">
            <CosignatureTable
              data={needsReviewQuery.data}
              isLoading={needsReviewQuery.isLoading}
              key="needs-review"
              onClearFilters={isFiltered ? resetFilters : undefined}
              onSelectedRowsChange={setSelectedRows}
              selectedIds={selectedIds}
            />
          </TabPanel>

          <TabPanel id="all-notes">
            <CosignatureTable
              data={allNotesQuery.data}
              isLoading={allNotesQuery.isLoading}
              key="all-notes"
              onClearFilters={isFiltered ? resetFilters : undefined}
              onSelectedRowsChange={setSelectedRows}
              selectedIds={selectedIds}
            />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </CosignatureModalProvider>
  );
}

/** This is required to render component within Tabs */
const SafeFilters = createHideableComponent(Filters);
